import {
  isServiceWorkerMissing,
  isServiceWorkerUpdateStarted,
  ServiceWorkerUpdateResult,
} from '../serviceWorkerUpdateResult';
import type { DeployedVersionWatch, DeployedVersionWatchOptions } from './types';

/** `index.html` is a few kilobytes: cheap enough for every open tab. */
export const DEPLOYED_VERSION_CHECK_INTERVAL_MS = 60 * 60_000;
/** Returning to the tab often (switching windows) checks at most this often. */
export const MIN_CHECK_INTERVAL_ON_RETURN_MS = 5 * 60_000;
/**
 * Each try downloads the worker script (about 1.5 MB): a version whose worker
 * does not install after this many tries waits for the next deploy.
 */
export const MAX_UPDATE_ATTEMPTS_PER_VERSION = 3;

/**
 * Tells an open page that a reload would run another version: the worker
 * script is fetched only then, not on every check.
 */
export function createDeployedVersionWatch({
  pageEntryScript,
  readDeployedEntryScript,
  onOtherVersionDeployed,
  onWatchFinished,
  isVisible,
  now,
}: DeployedVersionWatchOptions): DeployedVersionWatch {
  let lastCheckAt = now();
  let isCheckRunning = false;
  let isWatchFinished = false;
  let notStartedUpdate = { entryScript: '', attempts: 0 };

  const areUpdateAttemptsSpent = (deployedEntryScript: string): boolean =>
    notStartedUpdate.entryScript === deployedEntryScript && notStartedUpdate.attempts >= MAX_UPDATE_ATTEMPTS_PER_VERSION;

  const countNotStartedUpdate = (deployedEntryScript: string): void => {
    const isSameVersion = notStartedUpdate.entryScript === deployedEntryScript;
    notStartedUpdate = { entryScript: deployedEntryScript, attempts: isSameVersion ? notStartedUpdate.attempts + 1 : 1 };
  };

  const requestUpdate = async (deployedEntryScript: string): Promise<void> => {
    const result = await onOtherVersionDeployed(deployedEntryScript).catch(() => ServiceWorkerUpdateResult.notStarted);
    if (isServiceWorkerUpdateStarted(result) || isServiceWorkerMissing(result)) {
      isWatchFinished = true;
      onWatchFinished();
      return;
    }
    countNotStartedUpdate(deployedEntryScript);
  };

  const check = async (): Promise<void> => {
    isCheckRunning = true;
    lastCheckAt = now();
    try {
      const deployedEntryScript = await readDeployedEntryScript();
      if (
        deployedEntryScript === null ||
        deployedEntryScript === pageEntryScript ||
        areUpdateAttemptsSpent(deployedEntryScript)
      ) {
        return;
      }
      await requestUpdate(deployedEntryScript);
    } finally {
      isCheckRunning = false;
    }
  };

  const canCheck = (): boolean => !isWatchFinished && !isCheckRunning && isVisible();

  return {
    checkPeriodically: async () => {
      if (canCheck()) {
        await check();
      }
    },
    checkOnReturn: async () => {
      if (canCheck() && now() - lastCheckAt >= MIN_CHECK_INTERVAL_ON_RETURN_MS) {
        await check();
      }
    },
  };
}
